import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  CheckCircle2, XCircle, Clock, Mail, Building2, User, RefreshCw
} from "lucide-react";

export default function ModerationPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [details, setDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [rejectComment, setRejectComment] = useState("");
  const [processing, setProcessing] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const res = await axios.get("/api/admin/moderation");
      setCampaigns(res.data);
    } catch (err) {
      console.error(err);
      alert("Ошибка загрузки списка модерации");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCampaign = async (c) => {
    setSelected(c);
    setRejectComment("");
    setLoadingDetails(true);
    try {
      const res = await axios.get(`/api/admin/moderation/${c.id}`);
      setDetails(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleApprove = async () => {
    if (!selected) return;
    if (!window.confirm(`Одобрить и запустить кампанию #${selected.id}?`)) return;
    setProcessing(true);
    try {
      const res = await axios.post(`/api/admin/moderation/${selected.id}/approve`);
      alert(res.data.message);
      setSelected(null);
      setDetails(null);
      load();
    } catch (err) {
      alert("Ошибка: " + (err.response?.data?.error || err.message));
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selected) return;
    if (!rejectComment.trim()) {
      alert("Введите причину отклонения");
      return;
    }
    setProcessing(true);
    try {
      await axios.post(`/api/admin/moderation/${selected.id}/reject`, {
        comment: rejectComment.trim()
      });
      alert("Кампания отклонена");
      setSelected(null);
      setDetails(null);
      setRejectComment("");
      load();
    } catch (err) {
      alert("Ошибка: " + (err.response?.data?.error || err.message));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-4">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
          <Clock size={22} className="text-yellow-600" />
          Модерация кампаний
        </h1>
        <button
          onClick={load}
          disabled={loading}
          className="px-3 py-2 rounded-xl bg-white border hover:bg-gray-50 text-sm inline-flex items-center gap-2"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Обновить
        </button>
      </div>

      <div className="flex flex-col lg:flex-row gap-3">
        {/* Список */}
        <div className="w-full lg:w-1/3 space-y-2">
          {loading && <div className="text-gray-400 text-center py-8">Загрузка...</div>}
          {!loading && campaigns.length === 0 && (
            <div className="bg-white rounded-2xl shadow-md p-6 text-center text-gray-500 text-sm">
              Нет кампаний на модерации
            </div>
          )}
          {campaigns.map(c => (
            <button
              key={c.id}
              onClick={() => openCampaign(c)}
              className={`w-full text-left bg-white rounded-2xl shadow-md p-3 transition border-2 ${
                selected?.id === c.id ? "border-yellow-500" : "border-transparent hover:border-gray-200"
              }`}
            >
              <div className="font-semibold text-sm text-gray-800 truncate">
                #{c.id} — {c.name || "Без названия"}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                от {c.user_email} ({c.user_plan})
              </div>
              <div className="text-xs text-gray-400 mt-1 truncate">
                {c.subject || "(без темы)"}
              </div>
              <div className="text-[10px] text-gray-400 mt-1">
                {c.created_at && new Date(c.created_at).toLocaleString("ru-RU")}
              </div>
            </button>
          ))}
        </div>

        {/* Детали */}
        <div className="w-full lg:w-2/3">
          {!selected ? (
            <div className="bg-white rounded-2xl shadow-md p-8 text-center text-gray-500 text-sm">
              Выберите кампанию слева
            </div>
          ) : loadingDetails ? (
            <div className="bg-white rounded-2xl shadow-md p-8 text-center text-gray-400 text-sm">
              Загрузка деталей...
            </div>
          ) : details ? (
            <div className="bg-white rounded-2xl shadow-md p-4 space-y-4">
              <div className="border-b pb-3">
                <h2 className="text-lg font-bold">#{details.id} — {details.name}</h2>
                <div className="text-xs text-gray-500 mt-1">
                  <User size={12} className="inline" /> {details.user_email}
                </div>
              </div>

              {/* Тема */}
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase mb-1">
                  Тема письма
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-sm whitespace-pre-wrap">
                  {details.subject || "(пусто)"}
                </div>
              </div>

              {/* Тексты */}
              <div>
                <div className="text-xs font-semibold text-gray-500 uppercase mb-1">
                  Тексты ({Array.isArray(details.texts) ? details.texts.length : 0})
                </div>
                <div className="space-y-2">
                  {(Array.isArray(details.texts) ? details.texts : []).map((t, i) => (
                    <div key={i} className="bg-gray-50 rounded-xl p-3 text-sm whitespace-pre-wrap">
                      <div className="text-[10px] text-gray-400 mb-1">Вариант {i + 1}</div>
                      {t}
                    </div>
                  ))}
                </div>
              </div>

              {/* Метаданные */}
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="text-gray-500">База</div>
                  <div className="font-semibold">ID {details.database_id}</div>
                </div>
                <div>
                  <div className="text-gray-500">Контактов в базе</div>
                  <div className="font-semibold">{details.contacts_count ?? "—"}</div>
                </div>
                <div>
                  <div className="text-gray-500">Создана</div>
                  <div className="font-semibold">
                    {new Date(details.created_at).toLocaleString("ru-RU")}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Всего писем</div>
                  <div className="font-semibold">{details.total_count ?? 0}</div>
                </div>
              </div>

              {/* Кнопки */}
              <div className="border-t pt-4 space-y-3">
                <div className="flex gap-2">
                  <button
                    onClick={handleApprove}
                    disabled={processing}
                    className="flex-1 px-4 py-2.5 rounded-xl bg-green-600 text-white hover:bg-green-700 transition disabled:opacity-50 font-medium inline-flex items-center justify-center gap-2"
                  >
                    <CheckCircle2 size={16} /> Одобрить и запустить
                  </button>
                </div>

                <div className="bg-red-50 border border-red-200 rounded-xl p-3 space-y-2">
                  <div className="text-xs font-semibold text-red-800">
                    Отклонить с комментарием:
                  </div>
                  <textarea
                    value={rejectComment}
                    onChange={e => setRejectComment(e.target.value)}
                    rows={2}
                    placeholder="Например: слишком агрессивный текст, уберите слово 'гарантия'"
                    className="w-full border rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-red-400"
                  />
                  <button
                    onClick={handleReject}
                    disabled={processing || !rejectComment.trim()}
                    className="w-full px-4 py-2 rounded-xl bg-red-600 text-white hover:bg-red-700 transition disabled:opacity-50 text-sm font-medium inline-flex items-center justify-center gap-2"
                  >
                    <XCircle size={16} /> Отклонить
                  </button>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}